import type { JSX, ReactNode } from "react"
import { createContext, use, useState } from "react"

import { keyBy } from "lodash-es"
import { Form } from "react-router-dom"

import { RailsForm } from "~/src/components/RailsForm"
import type {
  ProductSelectionItem,
  ProductUserSelections,
  SelectionItemBase,
  StoreProductGroup,
} from "~/src/types/productSelection"

export type ItemSelectionFn = (item: ProductSelectionItem) => void
type SearchSetterFn = (s: string) => void

type SelectionFormPageProps = {
  formUrl: string
  storeProductGroups?: Array<StoreProductGroup>
  selections: Array<ProductSelectionItem>
  children?: ReactNode
  hiddenInputs: JSX.Element
  usingReactRouter?: boolean
}

const SelectionsContext = createContext<ProductUserSelections<SelectionItemBase> | null>(null)
const SelectionSetterContext = createContext<ItemSelectionFn | null>(null)
const SearchContext = createContext<string | null>(null)
const SearchSetterContext = createContext<SearchSetterFn | null>(null)

export const useSelectedItems = <Item extends SelectionItemBase>() => {
  const selectedItems = use(SelectionsContext) as ProductUserSelections<Item>

  if (!selectedItems) {
    throw new Error("useSelectedItems has to be used within <SelectionsContext.Provider>")
  }

  return selectedItems
}

export const useSetSelectedItem = () => {
  const setSelectedItem = use(SelectionSetterContext)

  if (!setSelectedItem) {
    throw new Error("useSetSelectedItem has to be used within <SelectionSetterContext.Provider>")
  }

  return setSelectedItem
}

export const useSearchQuery = () => {
  const search = use(SearchContext)

  if (search == null) {
    throw new Error("useSearchQuery has to be used within <SearchContext.Provider>")
  }

  return search
}

export const useSetSearchQuery = () => {
  const setSearch = use(SearchSetterContext)

  if (!setSearch) {
    throw new Error("setSearch has to be used within <SearchSetterContext.Provider>")
  }

  return setSearch
}

const existingUserSelections = (
  selections: Array<ProductSelectionItem>,
  storeProductGroups?: Array<StoreProductGroup>
) => {
  if (storeProductGroups == null) {
    return keyBy(selections, (selection) => selection.selectableId)
  }

  const selectableMap = new Map(
    storeProductGroups
      .map((spg) => spg.selectables)
      .flat()
      .map((sp) => [sp.id, sp])
  )
  return selections.reduce((acc, selection) => {
    const selectable = selectableMap.get(selection.selectableId)

    acc[selection.selectableId] = { ...selection, selectable }

    return acc
  }, {})
}

export function FormPage(props: SelectionFormPageProps) {
  const { usingReactRouter = false, formUrl, selections, storeProductGroups, hiddenInputs, children } = props

  const [selectedItems, setSelectedItems] = useState<ProductUserSelections>(() =>
    existingUserSelections(selections, storeProductGroups)
  )

  const [search, setSearch] = useState<string>("")

  const selectItem = (selectionItem: ProductSelectionItem) => {
    const { selectableId: id } = selectionItem
    const updatedValue = { ...selectedItems }
    updatedValue[id] = selectionItem
    setSelectedItems(updatedValue)
  }

  return (
    <SelectionsContext value={selectedItems}>
      <SelectionSetterContext value={selectItem}>
        <SearchContext value={search}>
          <SearchSetterContext value={setSearch}>
            {usingReactRouter ? (
              <Form method="patch" className="contents">
                {hiddenInputs}
                {children}
              </Form>
            ) : (
              <RailsForm method="patch" className="contents" action={formUrl}>
                {hiddenInputs}
                {children}
              </RailsForm>
            )}
          </SearchSetterContext>
        </SearchContext>
      </SelectionSetterContext>
    </SelectionsContext>
  )
}
